import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { defaultErrorResponses } from './errors';

const c = initContract();

// =====================================================
// SCHEMAS
// =====================================================

export const CreateCheckoutSessionRequestSchema = z.object({
  priceId: z.enum(['monthly', 'yearly'], {
    message: 'priceId must be "monthly" or "yearly"',
  }),
  source: z.enum(['cleanup-preview', 'pricing', 'upgrade', 'homepage']).optional(),
  referralId: z.string().max(120).optional(),
  utmSource: z.string().max(120).optional(),
});

export const CreateCheckoutSessionResponseSchema = z.object({
  sessionId: z.string(),
  checkoutUrl: z.string().url(),
});

export const SubscriptionStatusResponseSchema = z.object({
  status: z.enum(['none', 'active', 'trialing', 'past_due', 'canceled', 'paused', 'incomplete', 'incomplete_expired', 'unpaid']),
  planInterval: z.enum(['month', 'year']).nullable(),
  priceUsd: z.number().nullable(),
  productName: z.string().nullable(),
  currentPeriodEnd: z.string().datetime({ offset: true }).nullable(),
  cancelAtPeriodEnd: z.boolean(),
  stripeCustomerId: z.string().nullable(),
});

export const BillingPortalResponseSchema = z.object({
  portalUrl: z.string().url(),
});

// =====================================================
// CONTRACTS
// =====================================================

/**
 * Stripe Payment & Subscription Contract
 * Uses Supabase Stripe Sync Engine for automatic data sync (no webhook code needed)
 * Handles checkout sessions, subscription management, and billing portal access
 */
export const stripeContract = c.router({
  /**
   * Creates Stripe Checkout Session for subscription.
   * Gets: { priceId: 'monthly' | 'yearly' }.
   * Returns: { sessionId, checkoutUrl }.
   * Side Effects: Creates Stripe customer if not exists (stored in user_stripe_mapping),
   *   creates checkout session, generates redirect URL.
   * Error Codes: STRIPE_ERROR, UNAUTHORIZED.
   * Metadata: Sync Engine automatically syncs subscription data when checkout completes.
   */
  createCheckoutSession: {
    method: 'POST',
    path: '/v1/stripe/checkout',
    body: CreateCheckoutSessionRequestSchema,
    responses: {
      200: CreateCheckoutSessionResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Create Stripe Checkout session for subscription',
      description:
        'Creates a Stripe Checkout session for monthly or yearly subscription. Redirects to Stripe-hosted checkout page.',
    },
  },

  /**
   * Gets current subscription status for logged-in user.
   * Gets: auth header (user_id from JWT).
   * Returns: SubscriptionStatusResponseSchema { status, planInterval, priceUsd, productName, currentPeriodEnd, cancelAtPeriodEnd, stripeCustomerId }.
   * Side Effects: None (read-only). Queries user_stripe_mapping + synced stripe.subscriptions table.
   * Error Codes: UNAUTHORIZED.
   */
  getSubscriptionStatus: {
    method: 'GET',
    path: '/v1/stripe/subscription',
    responses: {
      200: SubscriptionStatusResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Get subscription status',
      description:
        'Returns current subscription status and plan details for the user. Data from Supabase Stripe Sync Engine.',
    },
  },

  /**
   * Creates Stripe Billing Portal session for managing subscription.
   * Gets: auth header (user_id from JWT).
   * Returns: { portalUrl }.
   * Side Effects: Creates portal session with Stripe, generates portal URL.
   * Error Codes: STRIPE_ERROR, UNAUTHORIZED.
   * Metadata: Portal allows users to manage invoices, payment methods, cancel subscriptions.
   */
  createBillingPortalSession: {
    method: 'POST',
    path: '/v1/stripe/portal',
    body: z.object({}),
    responses: {
      200: BillingPortalResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Create Stripe Billing Portal session',
      description:
        'Creates a Stripe Billing Portal session for managing invoices, payment methods, and subscriptions.',
    },
  },
});
